import { useState, useEffect, useRef } from "react";
import { useModalStore } from "../../../stores/modalStore.js";

function MiniBar({ value, max, color = '#6366f1' }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div style={{ flex: 1, height: 6, borderRadius: 3, background: 'rgba(255,255,255,0.07)', overflow: 'hidden' }}>
      <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 3, transition: 'width 0.6s ease' }} />
    </div>
  );
}

function StatCard({ icon, label, value, sub, color = '#6366f1' }) {
  return (
    <div style={{ padding: '16px 18px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ fontSize: 20 }}>{icon}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color, marginTop: 2 }}>{value?.toLocaleString?.() ?? value}</div>
      <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', fontWeight: 500 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>{sub}</div>}
    </div>
  );
}

function ActivityChart({ labels, messages, joins }) {
  const maxMsg = Math.max(...messages, 1);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {labels.map((day, i) => (
        <div key={day} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 28, fontSize: 11, color: 'rgba(255,255,255,0.4)', textAlign: 'right', flexShrink: 0 }}>{day}</div>
          <MiniBar value={messages[i]} max={maxMsg} color="#6366f1" />
          <div style={{ width: 36, fontSize: 11, color: 'rgba(255,255,255,0.5)', textAlign: 'right', flexShrink: 0 }}>{messages[i]}</div>
          <span style={{ fontSize: 10, color: '#22c55e', whiteSpace: 'nowrap', flexShrink: 0 }}>+{joins[i]}</span>
        </div>
      ))}
    </div>
  );
}

export function ServerInsightsModal() {
  const { isOpen, type, data, onClose } = useModalStore();
  const serverId = data?.serverId;

  const [insights, setInsights] = useState(null);
  const [loading,  setLoading]  = useState(false);

  useEffect(() => {
    if (!isOpen || type !== 'serverInsights' || !serverId) return;
    setLoading(true);
    fetch(`/api/servers/${serverId}/insights`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
    })
      .then(r => r.json())
      .then(d => { if (d.success) setInsights(d.insights); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [isOpen, type, serverId]);

  if (!isOpen || type !== 'serverInsights') return null;

  const ins = insights;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }} onClick={onClose}>
      <div style={{ width: '100%', maxWidth: 600, maxHeight: '88vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-primary,#13141f)', borderRadius: 16, boxShadow: '0 24px 64px rgba(0,0,0,0.6)', overflow: 'hidden' }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#fff' }}>📊 Server Insights</div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>Analytics for your community</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: 18, cursor: 'pointer' }}>✕</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 24 }}>
          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200, color: 'rgba(255,255,255,0.3)', fontSize: 13 }}>Loading analytics…</div>
          ) : !ins ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200, color: 'rgba(255,255,255,0.3)', fontSize: 13 }}>No data available</div>
          ) : (
            <>
              {/* Stat cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
                <StatCard icon="👥" label="Total Members"   value={ins.memberCount}    sub={`${ins.onlineCount} online now`}    color="#6366f1" />
                <StatCard icon="💬" label="Total Messages"  value={ins.messageCount}   sub="all time"                           color="#06b6d4" />
                <StatCard icon="🆕" label="New This Week"   value={ins.newMembersWeek} sub="joined in last 7 days"              color="#22c55e" />
                <StatCard icon="📈" label="Retention Rate"  value={`${ins.retentionRate}%`} sub="members who stayed 7+ days"   color="#f59e0b" />
              </div>

              {/* Activity chart */}
              <section>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14 }}>7-Day Activity</div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', marginBottom: 10 }}>■ Messages &nbsp; ▲ Joins</div>
                <ActivityChart labels={ins.activity.labels} messages={ins.activity.messages} joins={ins.activity.joins} />
              </section>

              {/* Top channels */}
              {ins.topChannels?.length > 0 && (
                <section>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>Top Channels</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {ins.topChannels.map((ch, i) => {
                      const max = ins.topChannels[0]?.messages || 1;
                      return (
                        <div key={ch.name} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{ width: 18, fontSize: 12, color: 'rgba(255,255,255,0.3)', textAlign: 'center' }}>#{i + 1}</div>
                          <span style={{ fontSize: 13, color: '#fff', minWidth: 90 }}>#{ch.name}</span>
                          <MiniBar value={ch.messages} max={max} color="#6366f1" />
                          <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', width: 46, textAlign: 'right', flexShrink: 0 }}>{ch.messages.toLocaleString()}</span>
                        </div>
                      );
                    })}
                  </div>
                </section>
              )}

              {/* Top members */}
              {ins.topMembers?.length > 0 && (
                <section>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>Top Contributors</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {ins.topMembers.map((m, i) => {
                      const max = ins.topMembers[0]?.messages || 1;
                      const medals = ['🥇', '🥈', '🥉'];
                      return (
                        <div key={m.name} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{ width: 20, fontSize: 14, textAlign: 'center' }}>{medals[i] || `#${i+1}`}</div>
                          <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg,#6366f1,#06b6d4)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
                            {m.name?.[0]?.toUpperCase()}
                          </div>
                          <span style={{ fontSize: 13, color: '#fff', flex: 1 }}>{m.name}</span>
                          <MiniBar value={m.messages} max={max} color="#06b6d4" />
                          <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', width: 46, textAlign: 'right', flexShrink: 0 }}>{m.messages.toLocaleString()}</span>
                        </div>
                      );
                    })}
                  </div>
                </section>
              )}

              {/* Boost */}
              {ins.boostLevel !== undefined && (
                <section>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Server Boosts</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: 'rgba(163,94,254,0.1)', border: '1px solid rgba(163,94,254,0.25)', borderRadius: 10 }}>
                    <span style={{ fontSize: 28 }}>🚀</span>
                    <div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: '#c084fc' }}>Level {ins.boostLevel}</div>
                      <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>{ins.boostCount} boosts active</div>
                    </div>
                  </div>
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
